import { useState } from "react";
import { useDispatch } from "react-redux";
import { IProgram } from "../../../interfaces/models/IEvent";
import {
  formatDayAndMonth,
  getLocalTimeFromTimestamp,
  getNowInDateTimeZone,
} from "../../../utils/dates";
import {
  sessionHasDifferentVenue,
  upsertProgramSession,
} from "../../../utils/programVenue";
import { groupProgramsByDay } from "../../../utils/groupProgramsByDay";
import PreviewComponente from "../../../components/SlateEditor/Preview";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useEventLoader } from "../../../hooks/Events/useEventLoader";
import { useEnrichedPrograms } from "../../../hooks/Events/useEnrichedPrograms";
import CircularPhoto from "../../../components/Atomos/CircularPhoto/CircularPhoto";
import { SpeakerTypeEnum } from "../../../Enums/EventEnums";
import EventHasEndedComponent from "./EventHasEnded";
import Pencil from "../../../components/Atomos/Icons/ViewMode/Pencil";
import SessionFormModal from "./SessionFormModal";
import {
  programIdentityKey,
  updateEventPrograms,
} from "../../../utils/updateEventPrograms";
import { toastError, toastPromise } from "../../../utils/toast";
import { NewEvent } from "../../../redux/states/event.state";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import { parseProgramDescription } from "../../../utils/parseProgramDescription";
import GoTo from "../../../assets/images&icons/go-to-svg.svg";

export default function MemberProgramTab() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const event = useEventLoader(id);
  const enrichedPrograms = useEnrichedPrograms(event?.program ?? []);
  const [showSessionForm, setShowSessionForm] = useState(false);
  const [editingSession, setEditingSession] = useState<IProgram | null>(null);
  const { isStaffMode } = useStaffMode();

  const isStaffView =
    location.pathname.includes("/events-staff/") || isStaffMode;

  if (!event) {
    return <div>Event not found</div>;
  }

  const eventTimeZone = event.timeZone || "UTC";
  const displayTimeZone = "UTC";
  const groupedPrograms = groupProgramsByDay(
    enrichedPrograms,
    displayTimeZone,
  );

  const nowInEventZone = getNowInDateTimeZone(eventTimeZone);
  const eventEndTime = event.endDate ? event.endDate * 1000 : 0;

  if (!isStaffView && eventEndTime > 0 && eventEndTime < nowInEventZone) {
    return (
      <EventHasEndedComponent
        event={event}
        groupedPrograms={groupedPrograms}
        eventTimeZone={displayTimeZone}
      />
    );
  }

  const resolveRawProgram = (program: IProgram): IProgram => {
    const rawPrograms = event?.program ?? [];
    return (
      rawPrograms.find(
        (p) => programIdentityKey(p) === programIdentityKey(program),
      ) ?? program
    );
  };

  const handleEditSession = (program: IProgram) => {
    setEditingSession(resolveRawProgram(program));
    setShowSessionForm(true);
  };

  const handleCloseSessionForm = () => {
    setShowSessionForm(false);
    setEditingSession(null);
  };

  const handleSaveSession = async (session: Partial<IProgram>) => {
    if (!event || !event.id) {
      toastError("Event ID is required to save changes");
      return;
    }

    const isEmpty = !session.title && !session.startTime && !session.endTime;

    if (isEmpty && !editingSession) {
      handleCloseSessionForm();
      return;
    }

    const eventId = event.id;
    const editingSessionSnapshot = editingSession;
    const savePromise = (async () => {
      const reloadedEvent = await updateEventPrograms(
        eventId,
        (currentPrograms) =>
          upsertProgramSession(
            currentPrograms,
            session as IProgram,
            editingSessionSnapshot,
          ),
      );

      dispatch(NewEvent(reloadedEvent));

      await new Promise((resolve) => setTimeout(resolve, 0));

      handleCloseSessionForm();
    })();

    try {
      await toastPromise(savePromise, {
        pending: "Saving session...",
        success: "Session saved successfully!",
        error: "Failed to save session",
      });
    } catch (error) {
      toastError(error as string);
    }
  };

  const handleDeleteSession = async () => {
    if (!editingSession) {
      handleCloseSessionForm();
      return;
    }

    if (!event || !event.id) {
      toastError("Cannot delete session");
      return;
    }

    const eventId = event.id;
    const editingSessionSnapshot = editingSession;

    try {
      const deletePromise = updateEventPrograms(eventId, (currentPrograms) =>
        currentPrograms.filter(
          (p) =>
            !(
              p.title === editingSessionSnapshot.title &&
              p.startTime === editingSessionSnapshot.startTime &&
              p.endTime === editingSessionSnapshot.endTime
            ),
        ),
      );

      const reloadedEvent = await toastPromise(deletePromise, {
        pending: "Deleting session...",
        success: "Session deleted successfully!",
        error: "Failed to delete session",
      });

      dispatch(NewEvent(reloadedEvent));

      handleCloseSessionForm();
    } catch (error) {
      toastError(error as string);
    }
  };

  if (showSessionForm) {
    return (
      <SessionFormModal
        onClose={handleCloseSessionForm}
        onSave={handleSaveSession}
        onDelete={handleDeleteSession}
        initialSession={editingSession}
        eventStartDate={event?.startDate}
        eventEndDate={event?.endDate}
        eventId={event?.id}
      />
    );
  }

  return (
    <div className="bg-[#F6F6F6] py-4">
      {Object.entries(groupedPrograms).map(([date, programs]) => (
        <div key={date}>
          <div className="border-b border-primary pb-0 flex-shrink-0 mb-4 w-fit pl-4 md:pl-8">
            <h3 className="text-[22px] font-normal text-gray-font leading-4 md:leading-5 mb-2 text-left font-sans">
              {formatDayAndMonth(date, displayTimeZone)}
            </h3>
          </div>
          {(programs[0]?.venueName || programs[0]?.location) && (
            <p className="text-[#474F56] font-Roboto font-[500] text-base text-left mb-2 ml-5">
              Venue:{" "}
              {[programs[0].venueName, programs[0].location]
                .filter(Boolean)
                .join(", ")}
            </p>
          )}
          {programs.map((program) => {
            const parsedDescription = program.description
              ? parseProgramDescription(program.description)
              : null;
            return (
              <div key={program.title || program.startTime}>
                {sessionHasDifferentVenue(program, programs[0]) && (
                  <p className="text-[#474F56] font-Roboto font-[500] text-base text-left mb-2 ml-5">
                    Venue:{" "}
                    {[program.venueName, program.location]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                )}
                <div className="flex flex-col md:flex-row md:gap-4 lg:gap-12 ml-5 mb-4 md:mb-0 lg:mb-4">
                  <div className="flex flex-col gap-2">
                    <p className="text-[#474F56] font-Roboto font-[400] text-lg text-left whitespace-nowrap">
                      {getLocalTimeFromTimestamp(
                        program.startTime,
                        displayTimeZone,
                      )}{" "}
                      -{" "}
                      {getLocalTimeFromTimestamp(
                        program.endTime,
                        displayTimeZone,
                      )}
                    </p>
                    {isStaffView ? (
                      <>
                        <div className="hidden lg:flex mb-4 md:gap-4 lg:gap-12">
                          <div className="min-w-[140px]">
                            <button
                              onClick={() => handleEditSession(program)}
                              className="flex items-center gap-2"
                            >
                              <span className="text-center font-decimal text-sm not-italic font-bold leading-5 uppercase text-accent-Default-dark">
                                EDIT SESSION
                              </span>
                              <div className="w-6 h-6 bg-[#0070CD] rounded-full flex items-center justify-center">
                                <Pencil width={14} height={14} color="white" />
                              </div>
                            </button>
                          </div>
                          <div></div>
                        </div>
                        <div className="lg:hidden ml-5 mb-4">
                          <button
                            onClick={() => handleEditSession(program)}
                            className="flex items-center gap-2"
                          >
                            <span
                              className="text-center font-decimal text-sm not-italic font-bold leading-5 uppercase text-[#004578]"
                              style={{ fontVariant: "all-small-caps" }}
                            >
                              EDIT SESSION
                            </span>
                            <div className="w-6 h-6 bg-[#0070CD] rounded-full flex items-center justify-center">
                              <Pencil width={14} height={14} color="white" />
                            </div>
                          </button>
                        </div>
                      </>
                    ) : (
                      program.requiredRegistration && (
                        <div className="flex items-center gap-2 cursor-pointer" onClick={() => id && navigate(`/events/${id}/registration/form`)}>
                          <p className="text-secondary text-left font-Roboto font-[600] text-base lg:text-lg underline underline-offset-2">Registration</p>
                          <img src={GoTo} alt="Go to" />
                        </div>
                      )
                    )}
                  </div>
                  <div>
                    <p className="text-[#474F56] text-left font-Roboto font-[600] text-lg mr-4 md:mr-0 ">
                      {program.title}
                    </p>
                    {parsedDescription && (
                      <div className="text-[#474F56] text-left mb-4 md:hidden lg:block mr-4 md:mr-0 [&_*]:!bg-transparent">
                        <PreviewComponente
                          value={parsedDescription}
                          showMessage={false}
                          className="!bg-transparent [&_p]:mb-0 [&_p]:text-[#474F56] [&_*]:!bg-transparent"
                        />
                      </div>
                    )}
                    <div className="hidden lg:block md:mr-4">
                      {program.speakers && program.speakers.length > 0 && (
                        <div className="flex flex-wrap gap-4 xl:gap-12 mb-4">
                          {program.speakers.map((speaker) => (
                            <div
                              key={speaker.personId}
                              className="flex items-center flex-col md:flex-row gap-4 lg:gap-8 xl:gap-10"
                            >
                              <div className="flex flex-col items-center gap-2">
                                <CircularPhoto
                                  personId={speaker.personId}
                                  photo={speaker.photo || null}
                                  alt={speaker.name || "Speaker"}
                                  containerClassName="mt-4 md:mt-0 mb-2 md:mb-0"
                                />
                                <p className="font-Roboto font-[400] text-sm md:text-base lg:text-lg text-[#474F56]">
                                  {
                                    SpeakerTypeEnum[
                                      speaker.speakerType as keyof typeof SpeakerTypeEnum
                                    ]
                                  }
                                </p>
                              </div>
                              <div className="flex flex-col items-center md:items-start gap-2 md:gap-0">
                                <p className="text-[#0070CD] text-left font-Roboto font-[600] text-lg lg:text-xl">
                                  {speaker.name}
                                </p>
                                <p className="text-[#474F56] text-left font-Roboto font-[400] text-sm md:mt-2">
                                  {speaker.title ?? "Partner"}
                                </p>
                                <p className="text-[#474F56] text-left font-Roboto font-[400] text-sm">
                                  {speaker.firmName ?? "Firm"}
                                </p>
                                {speaker.firmLogo ? (
                                  <img
                                    src={speaker.firmLogo}
                                    alt={speaker.firmName}
                                    className="h-10 max-w-[120px] w-auto md:mt-4 object-contain"
                                    loading="lazy"
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                ) : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {parsedDescription && (
                  <div className="text-[#474F56] text-left mb-4 md:block hidden lg:hidden ml-5 [&_*]:!bg-transparent">
                    <PreviewComponente
                      value={parsedDescription}
                      showMessage={false}
                      className="!bg-transparent [&_p]:mb-0 [&_p]:text-[#474F56] [&_*]:!bg-transparent"
                    />
                  </div>
                )}
                <div className="flex flex-wrap gap-4 mr-4 lg:hidden ml-5 mb-4">
                  {program.speakers && program.speakers.length > 0 && (
                    <>
                      {program.speakers.map((speaker) => (
                        <div
                          key={speaker.personId}
                          className="flex items-center flex-col md:flex-row gap-4 lg:gap-8 xl:gap-10 mb-4"
                        >
                          <div className="flex flex-col items-center gap-2">
                            <CircularPhoto
                              personId={speaker.personId}
                              photo={speaker.photo}
                              alt={speaker.name}
                              containerClassName="mt-4 md:mt-0 mb-2 md:mb-0"
                            />
                            <p className="font-Roboto font-[400] text-sm md:text-base lg:text-lg text-[#474F56]">
                              {
                                SpeakerTypeEnum[
                                  speaker.speakerType as keyof typeof SpeakerTypeEnum
                                ]
                              }
                            </p>
                          </div>
                          <div className="flex flex-col items-center md:items-start gap-0">
                            <p className="text-[#0070CD] text-left font-Roboto font-[600] text-lg lg:text-xl">
                              {speaker.name}
                            </p>
                            <p className="text-[#474F56] text-left font-Roboto font-[400] text-sm">
                              {speaker.title ?? "Partner"}
                            </p>
                            <p className="text-[#474F56] text-left font-Roboto font-[400] text-sm">
                              {speaker.firmName ?? "Firm"}
                            </p>
                            {speaker.firmLogo ? (
                              <img
                                src={speaker.firmLogo}
                                alt={speaker.firmName}
                                className="h-10 max-w-[120px] w-auto mt-4 object-contain"
                                loading="lazy"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

